import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  fetchAssistantSettings,
  saveAssistantSettings,
  AssistantSettings,
} from "../../../services/assistant-settings.service";
import { SettingsCard } from "../ui";

/**
 * Per-company AI assistant ("Tomas") configuration.
 *
 * Each company uses its own Anthropic API key, so usage is billed to the company
 * that incurs it — there is no shared key. The stored key is never returned in
 * full: the backend masks it to the last 4 characters, and submitting the masked
 * value back leaves the stored key untouched.
 */
/**
 * Selectable models. Exact API ids — a typo here means every request 404s, which
 * is why this is a select rather than a free-text field.
 */
const ASSISTANT_MODELS: { id: string; labelKey: string; fallback: string }[] = [
  {
    id: "claude-sonnet-5",
    labelKey: "assistant_model_sonnet",
    fallback: "Sonnet 5 — balanced speed and cost (recommended)",
  },
  {
    id: "claude-opus-5",
    labelKey: "assistant_model_opus",
    fallback: "Opus 5 — strongest reasoning, slower and pricier",
  },
  {
    id: "claude-haiku-4-5",
    labelKey: "assistant_model_haiku",
    fallback: "Haiku 4.5 — fastest and cheapest, simpler answers",
  },
];

export default function AssistantTab() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [settings, setSettings] = useState<AssistantSettings | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    fetchAssistantSettings(companyId)
      .then((data) => {
        setSettings(data);
        setApiKey(data.apiKey || "");
        setModel(data.model || "");
        setIsActive(!!data.isActive);
      })
      .catch(() => toast.error(t("assistant_settings_load_failed", { defaultValue: "Could not load assistant settings" })));
  }, [companyId, t]);

  const handleSubmit = async () => {
    if (!companyId) return;
    setIsSubmitting(true);
    try {
      const saved = await saveAssistantSettings(companyId, {
        apiKey: apiKey.trim() ? apiKey.trim() : undefined,
        model: model.trim(),
        isActive,
      });
      setSettings(saved);
      setApiKey(saved.apiKey || "");
      toast.success(t("saved", { defaultValue: "Saved" }));
    } catch {
      toast.error(t("assistant_settings_save_failed", { defaultValue: "Could not save assistant settings" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasStoredKey = !!settings?.apiKey;

  return (
    <SettingsCard
      description={t("assistant_settings_hint", {
        defaultValue:
          "Each company uses its own Anthropic API key — usage is billed to this company.",
      })}
    >
      <p className="mb-4 text-sm text-gray-600">{t("assistant_settings_used_by")}</p>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <label htmlFor="assistantApiKey" className="mb-1 block text-sm font-medium text-gray-700">
            {t("assistant_api_key", { defaultValue: "Anthropic API key" })}
          </label>
          <input
            id="assistantApiKey"
            type="password"
            autoComplete="off"
            placeholder={hasStoredKey ? settings?.apiKey || "" : "sk-ant-..."}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 md:text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            {hasStoredKey
              ? t("assistant_api_key_stored", {
                defaultValue: "A key is saved. Leave this field as-is to keep it, or paste a new one to replace it.",
              })
              : t("assistant_api_key_empty", {
                defaultValue: "No key saved yet — the assistant stays off until one is added.",
              })}
          </p>
        </div>

        <div>
          <label htmlFor="assistantModel" className="mb-1 block text-sm font-medium text-gray-700">
            {t("assistant_model", { defaultValue: "Model" })}
          </label>
          <select
            id="assistantModel"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 md:text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            <option value="">
              {t("assistant_model_default", {
                defaultValue: "Default (Sonnet 5)",
              })}
            </option>
            {ASSISTANT_MODELS.map((option) => (
              <option key={option.id} value={option.id}>
                {t(option.labelKey, { defaultValue: option.fallback })}
              </option>
            ))}
            {/* Keep an unknown stored value selectable so saving does not
                silently change a model set elsewhere. */}
            {model && !ASSISTANT_MODELS.some((o) => o.id === model) && (
              <option value={model}>{model}</option>
            )}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          {t("is_active")}
        </label>

        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
          disabled={isSubmitting}
        >
          {isSubmitting ? `${t("save")}...` : t("save")}
        </button>
      </form>
    </SettingsCard>
  );
}
